import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { clearDraft } from "../store/commonSlices/uiDraftsSlice";

/**
 * Restores both AntD form fields and external React states
 * Usage:
 *   useRestoreDraft(form, DRAFT_KEY, { setExtraStates });
 *
 * @param {object} form - AntD form instance
 * @param {string} key - Draft key name (e.g. "employeeFormDraft")
 * @param {object} options - Optional settings
 *   @param {boolean} autoClear - Whether to clear draft after restoring
 *   @param {function} setExtraStates - Callback to set external state values
 */
export function useRestoreDraft(form, key, { autoClear = true, setExtraStates, shouldRestore = true } = {}) {
  const dispatch = useDispatch();
  const draft = useSelector((s) => s.uiDrafts?.drafts?.[key]);

  useEffect(() => {
    if (!form || !key || !shouldRestore) return; // ✅ only run if allowed
    if (!draft) return;

    try {
      // 🔹 1. Restore AntD form fields first
      form.setFieldsValue(draft);

      // 🔹 2. Restore extra React-controlled states (if provided)
      if (setExtraStates) {
        const restored = {};

        // ✅ Parse and rehydrate JSON fields
        restored.selectedAllowances = draft.selected_allowances
          ? JSON.parse(draft.selected_allowances)
          : [];

        restored.allowanceValues = draft.allowance_values
          ? JSON.parse(draft.allowance_values)
          : [];

        // ✅ Experience — split back into array of objects
        if (draft.experience_employer) {
          const employers = draft.experience_employer.split(" | ");
          const jobTitles = (draft.experience_job_title || "").split(" | ");
          const startDates = (draft.experience_start_date || "").split(" | ");
          const endDates = (draft.experience_end_date || "").split(" | ");
          const responsibilities = (draft.experience_responsibilities || "").split(" | ");
          const achievements = (draft.experience_achievements || "").split(" | ");

          restored.experiences = employers.map((emp, i) => ({
            employer: emp || "",
            jobTitle: jobTitles[i] || "",
            startDate: startDates[i] || null,
            endDate: endDates[i] || null,
            responsibilities: responsibilities[i] || "",
            achievements: achievements[i] || "",
          }));
        }

        // ✅ Basic scalar / boolean fields (restore directly)
        restored.salary_update_date = draft.salary_update_date || null;
        restored.leavesTakenPrevYear = draft.leaves_taken_prev_year || 0;
        restored.leavesTakenCurrYear = draft.leaves_taken_curr_year || 0;
        restored.totalLeaveBalance = draft.total_leave_balance || 0;

        restored.clientReportingManager = draft.client_reporting_manager || null;
        restored.selectedClient = draft.client_name ? { name: draft.client_name } : null;

        // ✅ Boolean and small numeric values
        restored.hideSalaryDetails = !!draft.hide_salary_details;
        restored.use_client_policy = !!draft.use_client_policy;
        restored.acknowledged = !!draft.acknowledged;

        // ✅ Complex structured fields
        restored.companyAssets = draft.company_assets || [];
        restored.returnedAssets = draft.returned_assets || {};
        restored.trainingData = {
          completedCourses: draft.completed_courses || "",
          certifications: draft.certifications || "",
          upcomingTrainings: draft.upcoming_trainings || "",
          skillsDevelopment: draft.skills_development || "",
          professionalDevelopment: draft.professional_development || "",
          trainingModes: [
            !!draft.training_mode_online,
            !!draft.training_mode_in_person,
            !!draft.training_mode_hybrid,
          ],
          futureTrainingInterest: !!draft.future_training_interest,
        };

        // ✅ Health & Safety
        restored.health_assessment_completed = draft.health_assessment_completed ? "yes" : "no";
        restored.first_aid_training = !!draft.first_aid_training;

        // ✅ Apply restored values back to component states
        setExtraStates(restored);
      }

      if (autoClear) dispatch(clearDraft(key));
    } catch (err) {
      console.warn("Draft restore failed:", err);
    }
  }, [form, key, draft, dispatch, autoClear, setExtraStates]);
}

